/**
 * JWT claim shapes and role discriminator used by the admin app.
 *
 * Source citations:
 *   Scrips.Organization.Api/Controllers/OrganizationController.cs:59
 *     — HasClaim("sa", "1") gates SuperAdmin bypass
 *   Scrips.Organization.Api/Controllers/OrganizationController.cs:64
 *     — FindFirst("orgId") carries the scoping Guid for OrgAdmin
 *   Scrips.Organization.Api/Controllers/OrganizationV1Controller.cs:43
 *     — FindFirstValue("tenant") carries the tenant id on v1 endpoints
 *   Scrips.Organization.Api/Controllers/OrganizationController.cs:94
 *     — FindFirst("ns")?.Value == "1" toggles the new-structure variant
 *
 * Confluence caveat (discovery-report.md §0.6): the `sa` claim is not issued
 * by the current Identity service; superAdmin detection will only work once
 * DEV-2331 is merged to the live issuer.
 *
 * ── PROD-1781 hardening ───────────────────────────────────────────────────
 * `roleFromClaims` used to grant `orgAdmin` on the mere PRESENCE of `orgId`
 * or `tenant`, and never looked at `exp`. Both are tightened below.
 *
 * Why it mattered even though it was not exploitable: this module ships
 * INSIDE the PM bundle and `/admin/*` is a live route there
 * (`src/app/routes/index.tsx:52`), while `orgId` + `tenant` are claims that
 * EVERY PM token carries. The only thing standing between that and a granted
 * `orgAdmin` was that the admin store is separate and is filled solely by the
 * admin login page (`client_id: 'Scrips.Admin'`, which currently returns
 * `invalid_client` against this environment). That is one wiring change away
 * from being reachable, so the discriminator now fails CLOSED instead of
 * relying on an accident of composition.
 *
 * ⚠️ Claim-space warning — do NOT "fix" `ORG_ADMIN_ROLE_CLAIM` to `'1'`.
 * There are two unrelated role vocabularies in this product:
 *   - `user.role` from the REST profile: numeric strings, orgAdmin = "1"
 *     (see `src/auth/roles.ts`).
 *   - the JWT `orgRoles` claim: English labels, "OrgAdmin" / "Practitioner"
 *     (see `src/auth/jwt.ts:17-18`).
 * This file reads a JWT, so the label space is the correct one here. The
 * inverse mistake — using "OrgAdmin" where a numeric ID belonged — is the
 * documented bug that once hid the Logs tab from every real OrgAdmin.
 */

export interface AdminClaims {
  /** User id. */
  sub: string;
  /** '1' = SuperAdmin bypass. See OrganizationController.cs:59. */
  sa?: '1' | '0';
  /** Tenant identifier used by v1 endpoints. See OrganizationV1Controller.cs:43. */
  tenant?: string;
  /** Organization GUID used by legacy org-scoped endpoints. See OrganizationController.cs:64. */
  orgId?: string;
  /** '1' = new-structure variant. See OrganizationController.cs:94. */
  ns?: '1' | '0';
  /**
   * Identity's single role-label string (`orgRoles`, camelCase — see
   * `src/auth/jwt.ts:17-18`). This is the EXPLICIT role grant; `orgId` /
   * `tenant` are SCOPING claims and are not authority on their own.
   */
  orgRoles?: string;
  /** Unix seconds, token expiry. */
  exp: number;
}

export type AdminRole = 'superAdmin' | 'orgAdmin' | 'none';

/**
 * The `orgRoles` label that grants org-admin. Verbatim from the Identity
 * label space — see the claim-space warning in this file's header before
 * changing it.
 */
export const ORG_ADMIN_ROLE_CLAIM = 'OrgAdmin';

/**
 * True when the token is expired, or carries no usable `exp` at all.
 *
 * Fails CLOSED on a missing/unparseable expiry: `decodeAdminJwt` coerces an
 * absent `exp` to `0`, and a token that cannot say when it dies is not a
 * token we should derive authority from. Signature verification remains the
 * backend's job — this is only about not honouring a claim set we can already
 * see is stale.
 *
 * `now` is injectable so tests don't depend on the wall clock.
 */
export function areClaimsExpired(
  c: AdminClaims | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!c) return true;
  if (!Number.isFinite(c.exp) || c.exp <= 0) return true;
  return c.exp * 1000 <= now;
}

/**
 * Derive the admin role from a decoded token.
 *
 * PROD-1781: requires an EXPLICIT grant, and a live token.
 *   - expired / no `exp`            → 'none'
 *   - `sa === '1'`                  → 'superAdmin' (backend parity:
 *                                     OrganizationController.cs:59)
 *   - `orgRoles === 'OrgAdmin'` AND a scoping claim → 'orgAdmin'
 *   - anything else                 → 'none'
 *
 * The scoping claim is still required on top of the role label, because every
 * org-scoped endpoint reads `orgId` / `tenant` to scope its query
 * (OrganizationController.cs:64, OrganizationV1Controller.cs:43) — an
 * org-admin with nothing to be admin OF cannot be served.
 */
export function roleFromClaims(
  c: AdminClaims | null | undefined,
  now: number = Date.now(),
): AdminRole {
  if (!c) return 'none';
  if (areClaimsExpired(c, now)) return 'none';
  if (c.sa === '1') return 'superAdmin';
  if (c.orgRoles === ORG_ADMIN_ROLE_CLAIM && (c.orgId || c.tenant)) {
    return 'orgAdmin';
  }
  return 'none';
}
